import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Loader2, Sparkles, Check, X, ChevronDown, ChevronRight, Undo2, History, CalendarClock, Pencil } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ACTION_CLS = {
  combine: 'bg-violet-50 text-violet-700 border-violet-200',
  retire: 'bg-rose-50 text-rose-700 border-rose-200',
  improve: 'bg-amber-50 text-amber-700 border-amber-200',
};
const STATUS_CLS = {
  accepted: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  dismissed: 'bg-slate-50 text-slate-500 border-slate-200',
  undone: 'bg-sky-50 text-sky-600 border-sky-200',
};

const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) +
  ' ' + new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '');

const RecCard = ({ rec, onDecide, onEdit, busy, selected, onToggleSel }) => {
  const [showDraft, setShowDraft] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draftBody, setDraftBody] = useState('');
  const [draftTitle, setDraftTitle] = useState('');
  const canEdit = rec.status === 'pending' && (rec.action === 'combine' || rec.action === 'improve') && rec.body;
  const startEdit = () => {
    setDraftBody(rec.body.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim());
    setDraftTitle(rec.title || '');
    setEditing(true);
    setShowDraft(false);
  };
  const saveEdit = async () => {
    const ok = await onEdit(rec.idx, rec.action === 'combine' ? { title: draftTitle, body: draftBody } : { body: draftBody });
    if (ok) setEditing(false);
  };
  return (
    <div className="border border-slate-200 rounded-md p-2.5 space-y-1.5 bg-white" data-testid={`rfp-cleanup-rec-${rec.idx}`}>
      <div className="flex items-center gap-2 flex-wrap">
        {rec.status === 'pending' && (
          <input type="checkbox" className="accent-[#0F2040]" checked={selected} onChange={() => onToggleSel(rec.idx)}
            data-testid={`rfp-cleanup-select-${rec.idx}`} />
        )}
        <Badge variant="outline" className={`text-[9px] uppercase font-bold ${ACTION_CLS[rec.action]}`}>{rec.action}</Badge>
        <span className="text-[10px] text-slate-400">{rec.category}</span>
        <span className="flex-1" />
        {rec.status === 'pending' ? (
          <>
            <Button size="sm" disabled={busy} onClick={() => onDecide(rec.idx, 'accept')}
              className="h-6 text-[10px] px-2 bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid={`rfp-cleanup-accept-${rec.idx}`}><Check className="w-3 h-3 mr-1" />Accept</Button>
            <Button size="sm" variant="outline" disabled={busy} onClick={() => onDecide(rec.idx, 'dismiss')}
              className="h-6 text-[10px] px-2" data-testid={`rfp-cleanup-dismiss-${rec.idx}`}>
              <X className="w-3 h-3 mr-1" />Dismiss</Button>
          </>
        ) : (
          <>
            <Badge variant="outline" className={`text-[9px] ${STATUS_CLS[rec.status] || ''}`}
              data-testid={`rfp-cleanup-status-${rec.idx}`}>{rec.status}</Badge>
            {rec.status === 'accepted' && (
              <Button size="sm" variant="outline" disabled={busy} onClick={() => onDecide(rec.idx, 'undo')}
                className="h-6 text-[10px] px-2 text-sky-700 border-sky-200 hover:bg-sky-50"
                title="Restore archived entries / previous wording" data-testid={`rfp-cleanup-undo-${rec.idx}`}>
                <Undo2 className="w-3 h-3 mr-1" />Undo</Button>
            )}
          </>
        )}
      </div>
      <div className="flex items-center gap-1.5 flex-wrap">
        {rec.blocks.map(b => (
          <Badge key={b.id} variant="outline" className="text-[9px] bg-slate-50 text-slate-600 border-slate-200"
            title={b.title}>{b.capability_id || b.title.slice(0, 30)} · used {b.usage}x</Badge>
        ))}
      </div>
      <p className="text-[11px] text-slate-600">{rec.why}</p>
      {rec.status !== 'pending' && (rec.actioned_by || rec.undone_by) && (
        <p className="text-[9px] text-slate-400" data-testid={`rfp-cleanup-audit-${rec.idx}`}>
          {rec.actioned_by && `${rec.status === 'undone' ? 'accepted' : rec.status} by ${rec.actioned_by} · ${fmtWhen(rec.actioned_at)}`}
          {rec.undone_by && `${rec.actioned_by ? ' — ' : ''}undone by ${rec.undone_by} · ${fmtWhen(rec.undone_at)}`}
        </p>
      )}
      {rec.body && !editing && (
        <>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowDraft(s => !s)} className="text-[10px] text-[#4ABFCF] font-semibold flex items-center gap-0.5"
              data-testid={`rfp-cleanup-draft-toggle-${rec.idx}`}>
              {showDraft ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
              {rec.action === 'combine' ? `Merged draft${rec.title ? `: ${rec.title}` : ''}` : 'Proposed rewrite'}
            </button>
            {rec.edited_by && (
              <Badge variant="outline" className="text-[8px] bg-sky-50 text-sky-600 border-sky-200"
                title={`Edited by ${rec.edited_by}`} data-testid={`rfp-cleanup-edited-badge-${rec.idx}`}>edited</Badge>
            )}
            {canEdit && (
              <button onClick={startEdit} className="text-[10px] text-slate-400 font-semibold flex items-center gap-0.5 hover:text-[#0F2040]"
                data-testid={`rfp-cleanup-edit-btn-${rec.idx}`}>
                <Pencil className="w-2.5 h-2.5" />Edit draft
              </button>
            )}
          </div>
          {showDraft && (
            rec.original_body && rec.edited_by ? (
              <div className="grid grid-cols-2 gap-2" data-testid={`rfp-cleanup-draft-diff-${rec.idx}`}>
                <div className="bg-slate-50 border border-slate-100 rounded p-2">
                  <p className="text-[9px] font-bold text-slate-400 uppercase mb-1">AI original{rec.original_title ? ` — ${rec.original_title}` : ''}</p>
                  <p className="text-[10px] text-slate-500 whitespace-pre-wrap max-h-40 overflow-y-auto">
                    {rec.original_body.replace(/<[^>]+>/g, ' ')}
                  </p>
                </div>
                <div className="bg-sky-50 border border-sky-100 rounded p-2">
                  <p className="text-[9px] font-bold text-sky-600 uppercase mb-1">Edited by {rec.edited_by}{rec.title ? ` — ${rec.title}` : ''}</p>
                  <p className="text-[10px] text-slate-700 whitespace-pre-wrap max-h-40 overflow-y-auto">
                    {rec.body.replace(/<[^>]+>/g, ' ')}
                  </p>
                </div>
              </div>
            ) : (
              <p className="text-[10px] text-slate-600 bg-slate-50 border border-slate-100 rounded p-2 whitespace-pre-wrap max-h-40 overflow-y-auto">
                {rec.body.replace(/<[^>]+>/g, ' ')}
              </p>
            )
          )}
        </>
      )}
      {editing && (
        <div className="space-y-1.5 bg-slate-50 border border-slate-200 rounded p-2" data-testid={`rfp-cleanup-edit-form-${rec.idx}`}>
          {rec.action === 'combine' && (
            <input value={draftTitle} onChange={e => setDraftTitle(e.target.value)} maxLength={120}
              className="w-full h-7 text-[11px] border border-slate-200 rounded px-2 bg-white text-slate-700"
              placeholder="Merged capability title" data-testid={`rfp-cleanup-edit-title-${rec.idx}`} />
          )}
          <textarea value={draftBody} onChange={e => setDraftBody(e.target.value)} rows={6}
            className="w-full text-[10px] border border-slate-200 rounded p-2 bg-white text-slate-700 font-mono"
            data-testid={`rfp-cleanup-edit-body-${rec.idx}`} />
          <div className="flex justify-end gap-1.5">
            <Button size="sm" variant="outline" className="h-6 text-[10px] px-2" onClick={() => setEditing(false)} disabled={busy}
              data-testid={`rfp-cleanup-edit-cancel-${rec.idx}`}>Cancel</Button>
            <Button size="sm" className="h-6 text-[10px] px-2 bg-[#0F2040] hover:bg-[#1A3055] text-white"
              onClick={saveEdit} disabled={busy || draftBody.trim().length < 60}
              data-testid={`rfp-cleanup-edit-save-${rec.idx}`}>Save draft</Button>
          </div>
        </div>
      )}
    </div>
  );
};

export const CleanupDialog = ({ open, onOpenChange, categories, onChanged }) => {
  const [session, setSession] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [viewingId, setViewingId] = useState(null); // null = latest
  const [showHistory, setShowHistory] = useState(false);
  const [catId, setCatId] = useState('');
  const [sel, setSel] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const pollRef = useRef(null);
  const viewingRef = useRef(null);
  viewingRef.current = viewingId;

  const load = useCallback(async () => {
    try {
      const vid = viewingRef.current;
      const res = vid
        ? await axios.get(`${API_URL}/api/rfp/cleanup/sessions/${vid}`, { withCredentials: true })
        : await axios.get(`${API_URL}/api/rfp/cleanup`, { withCredentials: true });
      setSession(res.data?.session || null);
      return res.data?.session;
    } catch { return null; }
  }, []);

  const loadSessions = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/rfp/cleanup/sessions`, { withCredentials: true });
      setSessions(res.data?.sessions || []);
    } catch { /* non-admin or transient */ }
  }, []);

  const startPolling = useCallback(() => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      const s = await load();
      if (s?.status !== 'running') { clearInterval(pollRef.current); loadSessions(); }
    }, 4000);
  }, [load, loadSessions]);

  useEffect(() => {
    if (!open) { clearInterval(pollRef.current); return undefined; }
    setSel(new Set());
    load().then(s => { if (s?.status === 'running' && !viewingRef.current) startPolling(); });
    loadSessions();
    return () => clearInterval(pollRef.current);
  }, [open, viewingId, load, loadSessions, startPolling]);

  const start = async () => {
    setBusy(true);
    try {
      await axios.post(`${API_URL}/api/rfp/cleanup`, { category_ids: catId ? [catId] : [] }, { withCredentials: true });
      toast.success('Cleanup review started — recommendations will appear as the AI works through the library');
      setViewingId(null);
      await load();
      startPolling();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not start the cleanup review'); }
    finally { setBusy(false); }
  };

  const decide = async (idx, decision) => {
    setBusy(true);
    try {
      await axios.post(`${API_URL}/api/rfp/cleanup/${session.id}/items/${idx}/${decision}`, {}, { withCredentials: true });
      toast.success(decision === 'accept' ? 'Applied — library updated (sources archived where merged)'
        : decision === 'undo' ? 'Undone — original entries restored' : 'Dismissed');
      setSel(s => { const n = new Set(s); n.delete(idx); return n; });
      await load();
      loadSessions();
      if (decision !== 'dismiss' && onChanged) onChanged();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not apply'); }
    finally { setBusy(false); }
  };

  const editRec = async (idx, payload) => {
    setBusy(true);
    try {
      await axios.put(`${API_URL}/api/rfp/cleanup/${session.id}/items/${idx}`,
        { ...payload, body: `<p>${payload.body.trim()}</p>` }, { withCredentials: true });
      toast.success('Draft updated — accepting will use your wording');
      await load();
      return true;
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not save the draft'); return false; }
    finally { setBusy(false); }
  };

  const bulkAct = async (decision) => {
    setBusy(true);
    try {
      const res = await axios.post(`${API_URL}/api/rfp/cleanup/${session.id}/items/bulk`,
        { indices: [...sel], decision }, { withCredentials: true });
      toast.success(`${res.data.done} recommendation${res.data.done === 1 ? '' : 's'} ${decision === 'accept' ? 'applied' : 'dismissed'}`);
      setSel(new Set());
      await load();
      loadSessions();
      if (decision === 'accept' && onChanged) onChanged();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Bulk action failed'); }
    finally { setBusy(false); }
  };

  const toggleSel = (idx) => setSel(s => {
    const n = new Set(s);
    if (n.has(idx)) n.delete(idx); else n.add(idx);
    return n;
  });

  const recs = session?.recommendations || [];
  const pending = recs.filter(r => r.status === 'pending');
  const allSelected = pending.length > 0 && pending.every(r => sel.has(r.idx));
  const fmtEta = (s) => (s > 0 ? ` · ~${s < 60 ? Math.max(5, Math.round(s / 5) * 5) + 's' : Math.ceil(s / 60) + 'm'} left` : '');
  const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '');
  const pastSessions = sessions.filter(s => s.id !== (sessions[0]?.id || ''));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="rfp-cleanup-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#0F2040] flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#4ABFCF]" />Clean &amp; Consolidate
          </DialogTitle>
        </DialogHeader>
        <div className="flex items-center gap-2 flex-wrap">
          <select value={catId} onChange={e => setCatId(e.target.value)} disabled={session?.status === 'running'}
            className="h-8 text-xs border border-slate-200 rounded-md px-2 bg-white text-slate-700"
            data-testid="rfp-cleanup-category-select">
            <option value="">Whole library</option>
            {(categories || []).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <Button size="sm" onClick={start} disabled={busy || session?.status === 'running'}
            className="h-8 text-xs bg-[#0F2040] hover:bg-[#1A3055] text-white" data-testid="rfp-cleanup-start-btn">
            {session?.status === 'running' ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />Reviewing…</> : <><Sparkles className="w-3.5 h-3.5 mr-1" />Run AI review</>}
          </Button>
          {session?.status === 'running' && (
            <span className="text-[10px] text-slate-500" data-testid="rfp-cleanup-progress">
              {session.progress?.label} {session.progress?.pct != null ? `${session.progress.pct}%` : ''}{fmtEta(session.progress?.eta_seconds)}
            </span>
          )}
          {session?.status === 'failed' && <span className="text-[10px] text-red-600">{session.error}</span>}
          <span className="flex-1" />
          {sessions.length > 1 && (
            <button onClick={() => setShowHistory(h => !h)} className="text-[10px] text-slate-500 font-semibold flex items-center gap-1 hover:text-[#0F2040]"
              data-testid="rfp-cleanup-history-toggle">
              <History className="w-3 h-3" />History ({sessions.length - 1})
            </button>
          )}
        </div>
        <p className="text-[9px] text-slate-400 flex items-center gap-1">
          <CalendarClock className="w-2.5 h-2.5" />A whole-library review also runs automatically each quarter — admins are notified when it's ready.
        </p>
        {showHistory && pastSessions.length > 0 && (
          <div className="border border-slate-200 rounded-md divide-y divide-slate-100" data-testid="rfp-cleanup-history-list">
            {pastSessions.map(s => (
              <button key={s.id} onClick={() => { setViewingId(s.id); setShowHistory(false); }}
                className="w-full flex items-center gap-2 px-2.5 py-1.5 text-left hover:bg-slate-50"
                data-testid={`rfp-cleanup-history-item-${s.id}`}>
                <span className="text-[10px] font-semibold text-[#0F2040]">{fmtDate(s.created_at)}</span>
                {s.scheduled && <Badge variant="outline" className="text-[8px] bg-sky-50 text-sky-600 border-sky-200">quarterly</Badge>}
                <span className="text-[9px] text-slate-400 truncate">{s.created_by}</span>
                <span className="flex-1" />
                <span className="text-[9px] text-slate-500">{s.blocks_reviewed} reviewed · {s.recs} recs</span>
                {s.accepted > 0 && <Badge variant="outline" className="text-[8px] bg-emerald-50 text-emerald-700 border-emerald-200">{s.accepted} accepted</Badge>}
                {s.undone > 0 && <Badge variant="outline" className="text-[8px] bg-sky-50 text-sky-600 border-sky-200">{s.undone} undone</Badge>}
              </button>
            ))}
          </div>
        )}
        {viewingId && (
          <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded px-2 py-1">
            <span className="text-[10px] text-slate-600">Viewing past review from {fmtDate(session?.created_at)}{session?.scheduled ? ' (quarterly)' : ''}</span>
            <button onClick={() => setViewingId(null)} className="text-[10px] text-[#4ABFCF] font-semibold"
              data-testid="rfp-cleanup-view-latest">Back to latest</button>
          </div>
        )}
        {session?.status === 'ready' && (
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-[10px] text-slate-500" data-testid="rfp-cleanup-summary">
              Reviewed {session.blocks_reviewed} capabilities · {recs.length} recommendation{recs.length === 1 ? '' : 's'} · {pending.length} pending
              {recs.length === 0 ? ' — the library looks clean' : ''}
            </p>
            {session.scheduled && !viewingId && (
              <Badge variant="outline" className="text-[8px] bg-sky-50 text-sky-600 border-sky-200" data-testid="rfp-cleanup-scheduled-chip">quarterly review</Badge>
            )}
            <span className="flex-1" />
            {pending.length > 0 && (
              <>
                <label className="text-[10px] text-slate-600 flex items-center gap-1 cursor-pointer">
                  <input type="checkbox" className="accent-[#0F2040]" checked={allSelected}
                    onChange={() => setSel(allSelected ? new Set() : new Set(pending.map(r => r.idx)))}
                    data-testid="rfp-cleanup-select-all" />
                  Select all
                </label>
                <Button size="sm" disabled={busy || sel.size === 0} onClick={() => bulkAct('accept')}
                  className="h-6 text-[10px] px-2 bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="rfp-cleanup-bulk-accept"><Check className="w-3 h-3 mr-1" />Accept selected ({sel.size})</Button>
                <Button size="sm" variant="outline" disabled={busy || sel.size === 0} onClick={() => bulkAct('dismiss')}
                  className="h-6 text-[10px] px-2" data-testid="rfp-cleanup-bulk-dismiss">
                  <X className="w-3 h-3 mr-1" />Dismiss selected</Button>
              </>
            )}
          </div>
        )}
        <div className="space-y-2">
          {recs.map(rec => <RecCard key={rec.idx} rec={rec} onDecide={decide} onEdit={editRec} busy={busy}
            selected={sel.has(rec.idx)} onToggleSel={toggleSel} />)}
        </div>
      </DialogContent>
    </Dialog>
  );
};
